import { StickyBackBar } from '@/components/layout/StickyBackBar';
import { PageHeader } from '@/components/ui/PageHeader';
import { useUserSettings, useUpdateUserSettings } from '@/api/hooks/useUserSettings';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { getErrorMessage } from '@/utils/errorHandler';
import { usePageTitle } from '@/hooks/usePageTitle';
import { toast } from 'sonner';
import { Switch } from '@headlessui/react';

export function NotificationsPage() {
    usePageTitle('Notifications');
    const { data: settings, isLoading, error } = useUserSettings();
    const updateSettings = useUpdateUserSettings();

    const handleToggle = async (newValue: boolean) => {
        try {
            await updateSettings.mutateAsync({ nudgeEmailEnabled: newValue });
            toast.success('Settings saved successfully');
        } catch {
            toast.error('Failed to save settings');
        }
    };

    return (
        <>
            <StickyBackBar destinationLabel="Settings" backPath="/settings" />
            <div className="max-w-2xl space-y-6 pt-4">
                <PageHeader title="Notifications" />

                {isLoading && (
                    <div className="flex justify-center py-12">
                        <div className="text-gray-500 dark:text-gray-400">
                            Loading notification settings...
                        </div>
                    </div>
                )}

                {error && (
                    <ErrorAlert message={getErrorMessage(error)} />
                )}

                {settings && (
                    <div className="rounded-lg border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-secondary">
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex-1">
                                <h3 className="font-medium text-gray-900 dark:text-white">
                                    Monthly value reminder
                                </h3>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    Receive an email on the last day of each month reminding you to record your portfolio values
                                </p>
                            </div>
                            <Switch
                                checked={settings.nudgeEmailEnabled}
                                onChange={handleToggle}
                                disabled={updateSettings.isPending}
                                className={`${
                                    settings.nudgeEmailEnabled
                                        ? 'bg-accent dark:bg-primary-light'
                                        : 'bg-gray-200 dark:bg-gray-600'
                                } relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-accent focus:ring-offset-2 dark:focus:ring-primary-light dark:focus:ring-offset-secondary disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                <span className="sr-only">Enable monthly value reminder</span>
                                <span
                                    aria-hidden="true"
                                    className={`${
                                        settings.nudgeEmailEnabled ? 'translate-x-5' : 'translate-x-0'
                                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                                />
                            </Switch>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
